import { useState, useEffect, useRef } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Check,
  XCircle,
  Activity,
  ShieldAlert,
  Shield,
  ShieldCheck,
} from 'lucide-react';
import { QuestionResponse, useQuestions } from '../../../hooks/useQuestions';
import {
  currentAssessmentResponse,
  hasCurrentAssessmentAnswer,
} from '../../../utils/currentAssessment';

interface DrivingForcesChecklistProps {
  entityType: string;
  entityKey: string;
  companyId: string;
  title: string;
  className?: string;
  onScoreChange?: (score: number) => void;
}

export default function DrivingForcesChecklist({
  entityType,
  entityKey,
  companyId,
  title,
  className = '',
  onScoreChange,
}: DrivingForcesChecklistProps) {
  const { questions, loading, error, saveResponse, getQuestionResponse } = useQuestions(
    entityType,
    companyId,
    entityKey
  );

  // Local state to track optimistic updates for immediate UI feedback
  const [optimisticResponses, setOptimisticResponses] = useState<Record<string, boolean>>({});
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [doesNotApply, setDoesNotApply] = useState<boolean | null>(null);

  // Use ref to store the latest onScoreChange callback to avoid useEffect dependency issues
  const onScoreChangeRef = useRef(onScoreChange);
  onScoreChangeRef.current = onScoreChange;

  // Function to get volatility icon and styling (based on absolute count)
  const getVolatilityDisplay = (score: number, doesNotApply: boolean) => {
    if (doesNotApply) {
      return {
        icon: ShieldCheck,
        className: 'text-blue-700',
        bgClassName: 'bg-blue-100',
        label: 'Very Low',
      };
    }

    if (score >= 3) {
      return {
        icon: ShieldAlert,
        className: 'text-red-700',
        bgClassName: 'bg-red-100',
        label: 'Very High',
      };
    }

    if (score === 2) {
      return {
        icon: ShieldAlert,
        className: 'text-orange-700',
        bgClassName: 'bg-orange-100',
        label: 'High',
      };
    }

    if (score === 1) {
      return {
        icon: Activity,
        className: 'text-yellow-700',
        bgClassName: 'bg-yellow-100',
        label: 'Moderate',
      };
    }

    if (score === 0) {
      return {
        icon: ShieldCheck,
        className: 'text-blue-700',
        bgClassName: 'bg-blue-100',
        label: 'Very Low',
      };
    }

    return {
      icon: Shield,
      className: 'text-gray-500',
      bgClassName: 'bg-gray-100',
      label: 'Not Analyzed',
    };
  };

  // Find the "does not apply" question (identified by point: -1)
  const doesNotApplyQuestion = questions.find((q) => q.point === -1);

  // Filter out the "does not apply" question from regular questions
  const regularQuestions = questions.filter((q) => q.point !== -1);

  const handleDoesNotApplyToggle = async (isChecked: boolean) => {
    console.log('Does not apply toggle clicked:', { isChecked, entityKey, entityType });
    setDoesNotApply(isChecked);

    if (!doesNotApplyQuestion) {
      console.error('No "does not apply" question found for this entity type');
      return;
    }

    const response: QuestionResponse = {
      questionId: doesNotApplyQuestion.id,
      entityType,
      entityKey,
      response: { selectedOptions: isChecked ? ['does_not_apply'] : [] },
    };

    console.log('Saving response:', response);
    const success = await saveResponse(response);
    console.log('Save result:', success);

    if (!success) {
      console.error('Failed to save does not apply response');
      // Revert on failure
      setDoesNotApply(!isChecked);
    }
  };

  const handleQuestionToggle = async (questionId: string, isChecked: boolean) => {
    // Optimistically update the UI immediately
    setOptimisticResponses((prev) => ({
      ...prev,
      [questionId]: isChecked,
    }));

    const response: QuestionResponse = {
      questionId,
      entityType,
      entityKey,
      response: { selectedOptions: isChecked ? ['selected'] : [] },
    };

    const success = await saveResponse(response);
    if (!success) {
      console.error('Failed to save question response');
      // Revert optimistic update on failure
      setOptimisticResponses((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    } else {
      // Clear optimistic state once saved successfully (backend state will take over)
      setOptimisticResponses((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  const isQuestionChecked = (questionId: string): boolean => {
    // Check optimistic state first, then fall back to backend state
    if (optimisticResponses.hasOwnProperty(questionId)) {
      return optimisticResponses[questionId];
    }

    const answer = getQuestionResponse(questionId, entityKey);
    const response = answer ? currentAssessmentResponse(answer).response : null;
    return response?.selectedOptions?.includes('selected') === true;
  };

  // Load "does not apply" state from database on mount and when questions load
  useEffect(() => {
    if (!loading && entityKey && questions.length >= 0 && doesNotApplyQuestion) {
      console.log(
        'Loading does not apply state for:',
        entityKey,
        'questionId:',
        doesNotApplyQuestion.id
      );

      const response = getQuestionResponse(doesNotApplyQuestion.id, entityKey);
      console.log('Got response for does not apply:', response);

      const selectedOptions = response
        ? currentAssessmentResponse(response).response?.selectedOptions
        : undefined;
      const isDoesNotApply =
        selectedOptions && selectedOptions.length > 0 && selectedOptions[0] === 'does_not_apply';
      console.log('Setting doesNotApply to:', isDoesNotApply || false);

      setDoesNotApply(isDoesNotApply || false);
    } else if (!loading && !doesNotApplyQuestion) {
      console.log('No does not apply question found');
      setDoesNotApply(false);
    }
  }, [loading, entityKey, questions, doesNotApplyQuestion]); // Added doesNotApplyQuestion as dependency

  // Update current score whenever questions, responses, or optimistic responses change
  useEffect(() => {
    const calculateCurrentScore = () => {
      // If "does not apply" is checked, return special value
      if (doesNotApply) {
        return { score: 0, hasAnalysis: true }; // 0 volatility because it doesn't apply
      }

      const checkedCount = regularQuestions.filter((question) =>
        isQuestionChecked(question.id)
      ).length;
      const hasAnalysis = questions.some(
        (question) =>
          Object.prototype.hasOwnProperty.call(optimisticResponses, question.id) ||
          hasCurrentAssessmentAnswer(getQuestionResponse(question.id, entityKey))
      );

      // Use absolute count for volatility scoring (1=Moderate, 2=High, 3+=Very High)
      return { score: checkedCount, hasAnalysis };
    };

    if (regularQuestions.length > 0 && !loading) {
      const { score, hasAnalysis } = calculateCurrentScore();

      // Set the display score (-1 for not analyzed, actual score if analyzed)
      const displayScore = hasAnalysis ? score : -1;
      setCurrentScore(displayScore);

      // Report score change immediately
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(displayScore);
      }
    } else if (regularQuestions.length === 0 && !loading) {
      setCurrentScore(-1); // Show not analyzed when no questions

      // Report no analysis done
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(-1);
      }
    }
  }, [questions, optimisticResponses, loading, doesNotApply, entityKey]); // Removed onScoreChange and moved calculateScore logic inside

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center h-32 text-center">
            <div>
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 h-full ${className}`}>
      <div className="p-6 h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-orange-600" />
            {title}
          </h3>
          {(() => {
            const volatilityDisplay = getVolatilityDisplay(currentScore, doesNotApply || false);
            const IconComponent = volatilityDisplay.icon;
            return (
              <div
                className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${volatilityDisplay.bgClassName}`}
              >
                <IconComponent className={`h-4 w-4 ${volatilityDisplay.className}`} />
                <span className={`text-sm font-medium ${volatilityDisplay.className}`}>
                  {volatilityDisplay.label}
                </span>
              </div>
            );
          })()}
        </div>

        {regularQuestions.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No questions available</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <div className="h-full">
              {/* Single group with neutral styling */}
              <div className="space-y-3 h-full">
                {/* Group Header */}
                <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                  <TrendingUp className="h-4 w-4 text-gray-600" />
                  <h4 className="font-medium text-gray-800">
                    Factors driving change in {title.replace(' Analysis', '').toLowerCase()}
                  </h4>
                </div>

                {/* Does Not Apply Option */}
                <div className="ml-4">
                  <label className="flex items-start space-x-3 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer transition-colors bg-gray-50">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={doesNotApply || false}
                        onChange={(e) => handleDoesNotApplyToggle(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 transition-all duration-200 ${
                          doesNotApply
                            ? 'border-gray-500 bg-gray-500'
                            : 'border-gray-400 hover:border-gray-500'
                        }`}
                      >
                        {doesNotApply && <XCircle className="h-3 w-3 text-white m-0.5" />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 font-medium">
                        This driving force does not apply to our industry
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Check this if this category of change is not relevant to your business
                        environment
                      </p>
                    </div>
                  </label>
                </div>

                {/* Questions - Fixed height with proper overflow */}
                {!doesNotApply ? (
                  <div
                    className="space-y-2 ml-4 h-[calc(100%-140px)] overflow-y-auto pr-2"
                    style={{ scrollbarWidth: 'thin', scrollbarColor: '#D1D5DB #F9FAFB' }}
                  >
                    {regularQuestions.map((question) => {
                      const isChecked = isQuestionChecked(question.id);

                      return (
                        <label
                          key={question.id}
                          className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <div className="relative mt-0.5 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleQuestionToggle(question.id, e.target.checked)}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded border-2 transition-all duration-200 ${
                                isChecked
                                  ? 'border-orange-500 bg-orange-500'
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {isChecked && <Check className="h-3 w-3 text-white m-0.5" />}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 leading-relaxed">
                              {question.content}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ml-4 p-6 text-center text-gray-500">
                    <XCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm">
                      This driving force has been marked as not applicable to your industry.
                    </p>
                    <p className="text-xs mt-1">
                      Uncheck "Does not apply" above to analyze specific factors.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
